import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { statsApi, grantsApi } from "../lib/api.js";
import StatCard from "../components/StatCard.jsx";
import GrantTable from "../components/GrantTable.jsx";
import CreateGrantModal from "../components/CreateGrantModal.jsx";

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [grants, setGrants] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await statsApi.get();
      setStats(res.data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const loadGrants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(pagination.page));
      params.set("limit", String(pagination.limit));
      if (status) params.set("status", status);
      if (query) params.set("q", query);
      const res = await grantsApi.list(`?${params.toString()}`);
      setGrants(res.data.grants);
      setPagination(res.data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, status, query]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const onEvaluate = (grant) => {
    navigate(`/evaluate?grant=${grant.id}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Evaluation Dashboard</h1>
          <p className="text-sm text-ink-500">Monitor grant applications and review progress.</p>
        </div>
        {isAdmin && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New Grant
          </button>
        )}
      </div>

      {error && <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section aria-label="Global statistics" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Grants" value={stats?.totalGrants ?? "—"} accent="brand" />
        <StatCard label="Submissions" value={stats?.totalEvaluations ?? "—"} accent="ink" />
        <StatCard label="Avg Final Score" value={stats?.averageScore ?? "—"} accent="green" hint={`Range ${stats?.lowestScore ?? 0}–${stats?.highestScore ?? 0}`} />
        <StatCard label="Awaiting Review" value={stats?.pendingEvaluations ?? "—"} accent="amber" />
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input max-w-xs"
            placeholder="Search organization or title…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search grants"
          />
          <select
            className="input max-w-[180px]"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="in_review">In Review</option>
            <option value="evaluated">Evaluated</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {loading ? (
          <div className="card p-10 text-center text-sm text-ink-500">Loading grants…</div>
        ) : (
          <>
            <GrantTable grants={grants} onEvaluate={onEvaluate} />
            <div className="flex items-center justify-between text-sm text-ink-500">
              <span>
                Page {pagination.page} of {pagination.pages || 1} · {pagination.total} total
              </span>
              <div className="flex gap-2">
                <button
                  className="btn-secondary px-3 py-1.5 text-xs"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                >
                  Previous
                </button>
                <button
                  className="btn-secondary px-3 py-1.5 text-xs"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {showCreate && (
        <CreateGrantModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            loadGrants();
            loadStats();
          }}
        />
      )}
    </div>
  );
}
